import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';

import { DndDragHandleProvider } from '~/components/core/DndOrderedList/DndDragHandleContext';
import cn from '~/Utils/cn';

import type {
  DndDragHandleProps,
  DndOrderedListEdge,
  DndOrderedListItemData,
  DndOrderedListItemState,
} from './DndOrderedList.types';

interface DndOrderedListItemProps {
  itemId: string;
  index: number;
  count: number;
  instanceId: symbol;
  disabled?: boolean;
  showDropLine?: boolean;
  className?: string;
  /** Keyboard reorder: move this item to a new index (clamped by the caller). */
  onKeyboardMove: (toIndex: number) => void;
  children: (state: DndOrderedListItemState) => ReactNode;
}

const DndOrderedListItem: React.FC<DndOrderedListItemProps> = ({
  itemId,
  index,
  count,
  instanceId,
  disabled = false,
  showDropLine = false,
  className,
  onKeyboardMove,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Optional keyboard "grab": Enter/Space toggles it to announce a grabbed state to
  // assistive tech. The arrow keys move the row whether or not it's grabbed.
  const [isKeyboardDragging, setIsKeyboardDragging] = useState(false);
  const [dropEdge, setDropEdge] = useState<DndOrderedListEdge | null>(null);
  const [slotHeight, setSlotHeight] = useState(0);
  // Card geometry captured on drag-enter, before any slot is opened. Edge is computed
  // against this so the midpoint never moves as the slot grows/shrinks (no jitter).
  const naturalRect = useRef<{ top: number; height: number } | null>(null);

  const clearDropState = () => {
    setDropEdge(null);
    setSlotHeight(0);
    naturalRect.current = null;
  };

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    // When disabled, register no drag source or drop target so the row can't be
    // mouse-dragged at all (parity with the disabled up/down arrows this replaces).
    if (disabled) return undefined;

    const getEdge = (clientY: number): DndOrderedListEdge => {
      const rect = naturalRect.current ?? element.getBoundingClientRect();
      return clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
    };

    return combine(
      draggable({
        element,
        // The whole row is the drag source, so a mouse drag can start anywhere on it. The
        // handle stays the keyboard entry point (focus + arrow keys) for assistive tech.
        getInitialData: (): DndOrderedListItemData => ({ type: 'ordered-item', itemId, index, instanceId }),
        // Render a clean clone of the row as the drag preview. The browser's default preview
        // is a bitmap of the grabbed element, which bakes in any overlapping fixed chrome
        // (e.g. the sticky Save/Cancel footer) when the row sits under it. Cloning the row
        // onto its own surface captures just the card.
        onGenerateDragPreview: ({ location, nativeSetDragImage }) => {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({ element, input: location.current.input }),
            render: ({ container }) => {
              const rect = element.getBoundingClientRect();
              const clone = element.cloneNode(true) as HTMLElement;
              clone.style.width = `${rect.width}px`;
              clone.style.height = `${rect.height}px`;
              container.appendChild(clone);
            },
          });
        },
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        getData: ({ input }): DndOrderedListItemData => ({
          type: 'ordered-item',
          itemId,
          index,
          instanceId,
          closestEdge: getEdge(input.clientY),
        }),
        canDrop: ({ source }) => {
          const d = source.data as Partial<DndOrderedListItemData>;
          return d.type === 'ordered-item' && d.instanceId === instanceId;
        },
        onDragEnter: ({ source }) => {
          const { top, height } = element.getBoundingClientRect();
          naturalRect.current = { top, height };
          // The dragged card's height is fixed for the drag, so measure it once here
          // rather than on every onDrag frame (avoids forced synchronous layout).
          if ((source.data as Partial<DndOrderedListItemData>).itemId !== itemId) {
            setSlotHeight(source.element.getBoundingClientRect().height);
          }
        },
        onDrag: ({ self, source }) => {
          // No slot on the card being dragged itself.
          if ((source.data as Partial<DndOrderedListItemData>).itemId === itemId) {
            clearDropState();
            return;
          }
          setDropEdge((self.data as DndOrderedListItemData).closestEdge ?? null);
        },
        onDragLeave: clearDropState,
        onDrop: clearDropState,
      })
    );
  }, [itemId, index, instanceId, disabled]);

  const moveBy = (delta: number) => {
    if (disabled) return;
    const target = index + delta;
    if (target < 0 || target > count - 1) return;
    onKeyboardMove(target);
  };

  const handleHandleKeyDown = (event: React.KeyboardEvent) => {
    // Arrows move the focused row directly — grabbing first is optional.
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveBy(-1);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveBy(1);
      return;
    }
    // Enter/Space toggle the grabbed state (and stop the button's native click).
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsKeyboardDragging((prev) => !prev);
      return;
    }
    if (event.key === 'Escape' && isKeyboardDragging) {
      event.preventDefault();
      setIsKeyboardDragging(false);
    }
  };

  const dragHandleProps: DndDragHandleProps = {
    type: 'button',
    'aria-pressed': isKeyboardDragging,
    'aria-keyshortcuts': 'Enter Space ArrowUp ArrowDown Escape',
    'aria-label': isKeyboardDragging
      ? `Reordering item ${index + 1} of ${count}. Use the up and down arrow keys to move it, then press Enter, Space, or Escape to drop.`
      : `Reorder item ${index + 1} of ${count}. Use the up and down arrow keys to move it, or press Enter to grab.`,
    onKeyDown: handleHandleKeyDown,
    // Leaving the handle drops the grab so a later arrow press elsewhere isn't captured.
    onBlur: () => setIsKeyboardDragging(false),
  };

  return (
    <div
      ref={ref}
      className={cn('relative rounded-md outline-none transition-[padding] duration-150', {
        'cursor-grab': !disabled,
        'cursor-grabbing opacity-40': isDragging,
        'ring-2 ring-teal-600': isKeyboardDragging,
      })}
      style={{
        // Padding (not margin) keeps the card's top anchored and the slot inside the
        // drop target's hit area, so the pointer never falls into an untracked gap.
        paddingTop: dropEdge === 'top' ? slotHeight : undefined,
        paddingBottom: dropEdge === 'bottom' ? slotHeight : undefined,
      }}
    >
      {dropEdge && showDropLine ? (
        <div
          className="pointer-events-none absolute left-0 right-0 z-10 h-2 rounded-full bg-teal-600"
          style={dropEdge === 'top' ? { top: slotHeight / 2 } : { bottom: slotHeight / 2 }}
        />
      ) : null}
      <div className={className}>
        <DndDragHandleProvider value={dragHandleProps}>
          {children({ isDragging, isKeyboardDragging, dragHandleProps })}
        </DndDragHandleProvider>
      </div>
    </div>
  );
};

export default DndOrderedListItem;
